import { FEATURES } from '@/constants/feature-flags';
import {
  MARKETING_HOME_OPERATIONAL_METRICS,
  MARKETING_HOME_PROVIDER,
} from '@/content/marketing/home-mock-data';
import { useAuthHydration } from '@/hooks/use-auth-hydration';
import { isMarketingMockEnabled } from '@/lib/marketing-mock/is-marketing-mock-enabled';
import { getMarketingMockQueryOptions } from '@/lib/marketing-mock/query-options';
import api from '@/services/api';
import { OperationalMetrics } from '@/types/operational-metrics';
import { PlanUsage } from '@/types/plan-usage';
import { Provider } from '@/types/provider';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// Query keys
export const providerKeys = {
  all: ['provider'] as const,
  profile: () => [...providerKeys.all, 'profile'] as const,
  metrics: () => [...providerKeys.all, 'metrics'] as const,
  operationalMetrics: () => [...providerKeys.all, 'operational-metrics'] as const,
  planUsage: () => [...providerKeys.all, 'plan-usage'] as const,
};

// Provider profile
export function useProviderProfile(options?: { enabled?: boolean }) {
  const isHydrated = useAuthHydration();

  return useQuery({
    queryKey: providerKeys.profile(),
    queryFn: async () => {
      const response = await api.get<Provider>('/provider/me');
      return response.data;
    },
    // `enabled: false` ainda assina o cache: quem só quer ler o perfil já
    // buscado por outra tela (o `useTerms()` na sidebar do cliente) não
    // dispara `/provider/me` à toa.
    enabled: isHydrated && options?.enabled !== false,
    ...getMarketingMockQueryOptions(
      isMarketingMockEnabled() ? MARKETING_HOME_PROVIDER : undefined,
    ),
  });
}

// Operational metrics (dashboard)
export function useOperationalMetrics() {
  const isHydrated = useAuthHydration();

  return useQuery({
    queryKey: providerKeys.operationalMetrics(),
    queryFn: async () => {
      const response = await api.get<OperationalMetrics>('/dashboard/operational-metrics');
      return response.data;
    },
    enabled: isHydrated,
    ...getMarketingMockQueryOptions(
      isMarketingMockEnabled() ? MARKETING_HOME_OPERATIONAL_METRICS : undefined,
    ),
  });
}

export function useSubscription() {
  const isHydrated = useAuthHydration();

  return useQuery({
    queryKey: [...providerKeys.all, 'subscription'],
    queryFn: async () => {
      const response = await api.get<{
        id: string;
        status: string;
        currentPeriodEnd: number;
        /** Cancelamento já agendado para o fim do período pago. */
        cancelAtPeriodEnd?: boolean;
        /** Até quando cancelar devolve o valor pago (epoch, segundos); null depois de 7 dias. */
        refundableUntil?: number | null;
        planName: string;
        priceId: string;
        /** Limite do plano em texto (ex: "Até 200 clientes/mês") */
        capacity?: string | null;
        /** Clientes atendidos por mês (null = sem limite) */
        maxActiveClients?: number | null;
        /** Limite de fotos da galeria (null = sem limite) */
        maxPortfolioPhotos?: number | null;
      } | null>('/stripe/subscription');
      return response.data;
    },
    enabled: isHydrated && FEATURES.STRIPE_SUBSCRIPTIONS,
  });
}

/** Clientes atendidos no mês contra o limite do plano. */
export function usePlanUsage() {
  const isHydrated = useAuthHydration();

  return useQuery({
    queryKey: providerKeys.planUsage(),
    queryFn: async () => (await api.get<PlanUsage>('/provider/me/plan-usage')).data,
    enabled: isHydrated,
  });
}

export function useUpdateProvider() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<Provider>) => {
      const response = await api.patch<Provider>('/provider/me', data);
      return response.data;
    },
    onSuccess: (updatedProvider) => {
      queryClient.setQueryData(providerKeys.profile(), updatedProvider);
    },
  });
}
